import { useNavigate } from "react-router-dom";
import { Building2, Linkedin, MapPin } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { GroupChips, PersonAvatar } from "@/components/common/Visuals";
import { MembershipBadge } from "@/components/common/Dialogs";

const stop = (e) => e.stopPropagation();

function LinkedinLink({ url, id }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer" onClick={stop} data-testid={`person-linkedin-${id}`}
      className="flex h-8 w-8 items-center justify-center rounded-full text-[#0A66C2] transition-colors hover:bg-blue-50" aria-label="LinkedIn">
      <Linkedin className="h-4 w-4" />
    </a>
  );
}

export function PersonCard({ person, selectable, selected, onToggle }) {
  const navigate = useNavigate();
  const org = person.organizations?.[0];
  return (
    <div
      data-testid={`person-card-${person.id}`}
      onClick={() => navigate(`/personnes/${person.id}`)}
      className={`lm-card lm-card-hover group relative flex cursor-pointer flex-col p-5 ${selected ? "border-primary/50 ring-2 ring-primary/15" : ""}`}
    >
      {selectable && (
        <div className="absolute right-4 top-4" onClick={stop}>
          <Checkbox checked={selected} onCheckedChange={() => onToggle(person.id)} data-testid={`person-select-${person.id}`} aria-label="Sélectionner" />
        </div>
      )}
      <div className="flex items-start gap-4 pr-6">
        <PersonAvatar person={person} />
        <div className="min-w-0">
          <h3 className="truncate text-[17px] font-bold leading-snug" data-testid="person-card-name">{person.full_name}</h3>
          {person.job_title && <p className="mt-0.5 line-clamp-2 text-sm text-stone-600">{person.job_title}</p>}
        </div>
      </div>
      <div className="mt-4 space-y-1.5 text-sm text-stone-700">
        {org && (
          <div className="flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-stone-400" /><span className="truncate font-medium">{person.organizations.map((o) => o.name).join(", ")}</span></div>
        )}
        {person.city && <div className="flex items-center gap-2 text-stone-500"><MapPin className="h-3.5 w-3.5 text-stone-400" />{person.city}</div>}
      </div>
      <div className="mt-4 flex-1"><GroupChips person={person} /></div>
      <div className="mt-4 flex min-h-[36px] items-center justify-between border-t border-border/70 pt-3">
        <MembershipBadge value={person.membership} />
        <LinkedinLink url={person.linkedin} id={person.id} />
      </div>
    </div>
  );
}

export function PersonTable({ persons, selectable, selectedIds, onToggle, internal }) {
  const navigate = useNavigate();
  return (
    <div className="lm-card overflow-hidden">
      <Table data-testid="persons-table">
        <TableHeader>
          <TableRow className="bg-stone-50/80 hover:bg-stone-50/80">
            {selectable && <TableHead className="w-10" />}
            <TableHead>Nom</TableHead>
            <TableHead>Fonction</TableHead>
            <TableHead>Entreprise</TableHead>
            {internal && <TableHead>Ville</TableHead>}
            <TableHead>Communautés</TableHead>
            {internal && <TableHead>Adhésion</TableHead>}
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {persons.map((p) => (
            <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/personnes/${p.id}`)} data-testid={`person-row-${p.id}`}>
              {selectable && (
                <TableCell onClick={stop}>
                  <Checkbox checked={selectedIds.has(p.id)} onCheckedChange={() => onToggle(p.id)} data-testid={`person-row-select-${p.id}`} />
                </TableCell>
              )}
              <TableCell>
                <div className="flex items-center gap-3">
                  <PersonAvatar person={p} size="sm" className="rounded-xl" />
                  <span className="font-semibold">{p.full_name}</span>
                </div>
              </TableCell>
              <TableCell className="max-w-[240px] text-stone-600"><span className="line-clamp-2">{p.job_title}</span></TableCell>
              <TableCell className="font-medium">{p.organizations.map((o) => o.name).join(", ")}</TableCell>
              {internal && <TableCell className="text-stone-600">{p.city}</TableCell>}
              <TableCell className="max-w-[260px]"><GroupChips person={p} max={2} /></TableCell>
              {internal && <TableCell><MembershipBadge value={p.membership} /></TableCell>}
              <TableCell><LinkedinLink url={p.linkedin} id={p.id} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
